import {
  FC,
  memo,
  useContext,
  useRef,
  useState,
  useLayoutEffect,
  CSSProperties,
} from 'react';

import { useQuery } from '@apollo/client';
import {
  AlienIcon,
  CheckedCalendarIcon,
  ClockIcon,
  GhostIcon,
  Paragraph,
  Separator,
  SmartGamerDashedIcon,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { truncate } from 'lodash';
import { createPortal } from 'react-dom';

import { AuthContext } from 'auth';
import { SessionRestTimer } from 'components';
import { DEFAULT_GROUP_COLOR } from 'consts';
import { ESubscription, FeaturesContext } from 'features';
import { LocalizationContext } from 'localization';
import { getActiveSeance } from 'utils';

import { getHostTypeIcon } from '../../utils';
import {
  HostGroupRequest,
  HostGroupResponse,
  HostRequest,
  HostResponse,
} from '../interfaces';
import { HOST, HOST_GROUP } from '../schema';
import { IProps } from './interfaces';
import s from './Tooltip.module.scss';
import {
  formatTime,
  getHostHeader,
  formatBookingTime,
  getBookingDuration,
} from './utils';

export const Tooltip: FC<IProps> = memo(
  ({ id, setClientUUID, openBooking, anchorRect }) => {
    const { t } = useContext(LocalizationContext);
    const { subscription } = useContext(FeaturesContext);
    const ref = useRef<HTMLDivElement>(null);
    const {
      data: { permissions },
    } = useContext(AuthContext);

    const { data: hostResponse } = useQuery<HostResponse, HostRequest>(HOST, {
      fetchPolicy: 'cache-only',
      variables: { id },
    });

    const { data: groupResponse } = useQuery<
      HostGroupResponse,
      HostGroupRequest
    >(HOST_GROUP, {
      fetchPolicy: 'cache-only',
      variables: { id: hostResponse?.hostOverview.group_id ?? 0 },
      skip: !hostResponse?.hostOverview,
    });

    const [finalStyle, setFinalStyle] = useState<CSSProperties>({
      position: 'fixed',
      zIndex: 10000,
    });

    useLayoutEffect(() => {
      if (!ref.current || !anchorRect) return;

      const tooltipRect = ref.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const SPACE = 8;

      let top: number | undefined;
      let bottom: number | undefined;
      let left: number | undefined;
      let right: number | undefined;

      const spaceBelow = viewportHeight - anchorRect.bottom;
      if (spaceBelow >= tooltipRect.height + SPACE) {
        top = anchorRect.bottom + SPACE;
      } else {
        const spaceAbove = anchorRect.top;
        if (spaceAbove >= tooltipRect.height + SPACE) {
          bottom = viewportHeight - anchorRect.top + SPACE;
          top = undefined;
        } else {
          top = anchorRect.bottom + SPACE;
        }
      }
      const tooltipWidth = tooltipRect.width;

      if (anchorRect.left + tooltipWidth + SPACE <= viewportWidth) {
        left = anchorRect.left;
      } else {
        right = viewportWidth - anchorRect.right;
        left = undefined;

        if (anchorRect.right - tooltipWidth - SPACE < 0) {
          left = SPACE;
          right = undefined;
        }
      }

      setFinalStyle((prev) => ({
        ...prev,
        top,
        bottom,
        left,
        right,
      }));
    }, [anchorRect]);

    if (!hostResponse?.hostOverview || !anchorRect) return null;

    const {
      position,
      user,
      client_sessions: [activeSession],
      bookings: [nearestBooking],
      online,
      locked,
      device_has_changed,
      online_booking_enabled,
      type,
    } = hostResponse.hostOverview;

    const hostHeader = getHostHeader(hostResponse.hostOverview);
    const activeSeance = getActiveSeance(activeSession?.seances ?? []);
    const remainTime = activeSession?.time_left;
    const groupColor =
      groupResponse?.hostGroupOverview.customization?.color ||
      DEFAULT_GROUP_COLOR;

    return createPortal(
      <div
        ref={ref}
        className={cn(s.container, locked && s.locked)}
        style={
          {
            '--group-color': groupColor,
            '--group-color-10': groupColor + '1A',
            ...finalStyle,
          } as CSSProperties
        }
        onContextMenu={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={cn(s.row, s.header, !online && s.offline)}>
          {hostHeader.icon}
          {t(hostHeader.locale)}
          {activeSession && (
            <span className={s.rest}>
              {activeSession.finished_at ? (
                <SessionRestTimer
                  rest={remainTime}
                  className={s.rest}
                  showMinimal={false}
                />
              ) : (
                '∞'
              )}
            </span>
          )}
        </div>

        {locked && (
          <div className={cn(s.info, s.gray)}>
            {t(
              subscription === ESubscription.FREE
                ? 'host.locked.description'
                : 'host.locked.description.free'
            )}
          </div>
        )}

        {device_has_changed && (
          <div className={cn(s.info, s.orange)}>{t('host.config.new')}</div>
        )}

        <Separator />

        {activeSession && (
          <div className={cn(s.row, !online && s.offline)}>
            <ClockIcon size="m" color="primary" className={s.icon} />
            <div className={s.block}>
              {truncate(activeSeance?.tariff.title, {
                length: 18,
                separator: '...',
              })}
              <span className={s.additional}>
                {formatTime(activeSession.started_at)} →{' '}
                {activeSession.finished_at
                  ? formatTime(activeSession.finished_at)
                  : '∞'}
              </span>
            </div>
          </div>
        )}

        {user ? (
          <div className={cn(s.row, !online && s.offline)}>
            <AlienIcon size="m" color="primary" />
            <div className={s.block}>
              <span
                className={cn(
                  s.nickname,
                  !permissions?.has('client_info') && s.nickname_disabled
                )}
                onClick={() => {
                  if (!permissions?.has('client_info')) return;
                  setClientUUID(user.uuid);
                }}
              >
                {user.nickname}
              </span>
              <span className={s.additional}>{user.phone}</span>
            </div>
          </div>
        ) : activeSession ? (
          <div className={cn(s.row, !online && s.offline)}>
            <GhostIcon size="m" color="primary" />
            <div className={s.block}>{t('client.guest')}</div>
          </div>
        ) : null}

        {nearestBooking && (
          <div className={cn(s.row, !online && s.offline)}>
            <CheckedCalendarIcon size="m" color="primary" />
            <div className={s.block}>
              <div>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    openBooking(nearestBooking.id);
                  }}
                  className={s.booking_clicked}
                >
                  {t('booking.contracted')}{' '}
                  {formatBookingTime(nearestBooking.from, t)}{' '}
                </span>
                <span className={s.bookingDuration}>
                  {getBookingDuration(
                    nearestBooking.from,
                    nearestBooking.to,
                    t
                  )}
                </span>
              </div>
              <span className={s.additional}>
                {nearestBooking.hosts.length} {t('pc')}
              </span>
            </div>
          </div>
        )}

        {(nearestBooking || activeSession) && <Separator />}

        <div className={cn(s.row, s.footer, !online && s.offline)}>
          <span className={s.icon_block}>{getHostTypeIcon(type)}</span>
          <div className={s.color} />
          {groupResponse?.hostGroupOverview.title} • {position}
        </div>

        {!online_booking_enabled && (
          <div className={s.row}>
            <SmartGamerDashedIcon size="s" color="primary" />
            <Paragraph size="s" color="secondary">
              {t('host.online_booking.not_enabled')}
            </Paragraph>
          </div>
        )}
      </div>,
      document.body
    );
  }
);
